import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Check, Send } from 'lucide-react';

/**
 * "Ask about this" (B-49 slice 1, v47) — replaces the dead "Contact kit
 * manager" button in the Shop's item pop-up. Size (when the item has sizes),
 * quantity and an optional note → POST /clubs/:slug/shop/items/:id/enquiries.
 * The server emails the club's kit contact (and the member a copy) and lists
 * it in Club Admin → Shop → Enquiries. Adults only, a few an hour — the
 * server says so if the member can't.
 */
const NOTE_MAX = 300;
const QTY_MAX = 20;

const errText = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error || fallback;

export default function AskAboutThis({ clubSlug, clubName, clubVerified = true, product, onCancel }) {
  const sizes = Array.isArray(product.sizes) ? product.sizes.filter(Boolean) : [];
  const [size, setSize] = useState(sizes.length === 1 ? sizes[0] : '');
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState('');
  const [notMember, setNotMember] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (sizes.length && !size) { setError('Choose a size.'); return; }
    setSending(true);
    try {
      const r = await axios.post(`/clubs/${clubSlug}/shop/items/${product.id}/enquiries`, {
        ...(sizes.length ? { size } : {}),
        quantity,
        note: note.trim(),
      });
      setSent(r.data?.message || 'Sent to the club\'s kit contact — they\'ll reply to you by email.');
    } catch (err) {
      if (err?.response?.status === 403 && err.response.data?.reason === 'not_member') setNotMember(true);
      setError(errText(err, 'Could not send your enquiry — try again.'));
    } finally { setSending(false); }
  };

  if (sent) {
    return (
      <div className="shop-ask-sent" role="status">
        <Check size={18} />
        <div>
          <strong>Enquiry sent.</strong>
          <p>{sent}</p>
        </div>
      </div>
    );
  }

  return (
    <form className="shop-ask" onSubmit={submit}>
      <div className="shop-ask-row">
        {sizes.length > 0 ? (
          <div className="form-group">
            <label className="label" htmlFor="ask-size">Size</label>
            <select id="ask-size" className="input" value={size} onChange={e => setSize(e.target.value)}>
              <option value="">Choose a size</option>
              {sizes.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        ) : <div />}
        <div className="form-group">
          <span className="label" id="ask-qty-label">Quantity</span>
          <div className="shop-ask-qty" role="group" aria-labelledby="ask-qty-label">
            <button type="button" aria-label="One fewer" disabled={quantity <= 1} onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</button>
            <span aria-live="polite">{quantity}</span>
            <button type="button" aria-label="One more" disabled={quantity >= QTY_MAX} onClick={() => setQuantity(q => Math.min(QTY_MAX, q + 1))}>+</button>
          </div>
        </div>
      </div>
      <div className="form-group" style={{ margin: 0 }}>
        <label className="label" htmlFor="ask-note">Note <span className="optional">(optional)</span></label>
        <textarea id="ask-note" className="input" rows={2} maxLength={NOTE_MAX} value={note}
          placeholder="e.g. For Sam in the U11s — can I collect at training?" onChange={e => setNote(e.target.value)} />
        <p className="kit-hint" style={{ textAlign: 'right', display: 'block' }}>{note.length}/{NOTE_MAX}</p>
      </div>
      <p className="kit-hint" style={{ display: 'block', margin: 0 }}>
        {/* Unverified clubs' enquiries aren't emailed (mail-relay guard) — they wait in Club Admin. */}
        {clubVerified
          ? "This goes to the club's kit contact by email, with your name and email address so they can reply. Nothing is paid or ordered yet."
          : "This goes to the club's admins in the app, with your name and email address so they can reply. Nothing is paid or ordered yet."}
      </p>
      {error && <div className="kit-error">{error}</div>}
      {notMember && (
        <Link to={`/${clubSlug}`} className="btn btn-secondary" style={{ justifyContent: 'center' }}>
          Join {clubName || 'the club'} to ask about items
        </Link>
      )}
      <div className="kit-row">
        <button type="submit" className="btn btn-primary" disabled={sending}><Send size={14} /> {sending ? 'Sending…' : 'Send enquiry'}</button>
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={sending}>Cancel</button>
      </div>
    </form>
  );
}
